/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  InternalSessionIssuerKey,
  InternalSessionIssuerKey$inboundSchema,
} from "./internalsessionissuerkey.js";

/**
 * unavailable: not offered to this organization yet. verified_domain_required: needs at least one verified email domain. setup_required: not enabled yet. enabled: Speakeasy signs with it. disabled: an admin turned signing off. signing_paused: enabled, but Speakeasy has paused all signing. keyless: every key was revoked after a compromise and no replacement is active yet.
 */
export const InternalSessionIssuerState = {
  Unavailable: "unavailable",
  VerifiedDomainRequired: "verified_domain_required",
  SetupRequired: "setup_required",
  Enabled: "enabled",
  Disabled: "disabled",
  SigningPaused: "signing_paused",
  Keyless: "keyless",
} as const;
/**
 * unavailable: not offered to this organization yet. verified_domain_required: needs at least one verified email domain. setup_required: not enabled yet. enabled: Speakeasy signs with it. disabled: an admin turned signing off. signing_paused: enabled, but Speakeasy has paused all signing. keyless: every key was revoked after a compromise and no replacement is active yet.
 */
export type InternalSessionIssuerState = OpenEnum<
  typeof InternalSessionIssuerState
>;

export type InternalSessionIssuer = {
  /**
   * Whether deployment readiness, rollout and a verified email domain currently allow enabling this issuer. The caller still needs organization administrator access.
   */
  canEnable: boolean;
  /**
   * Issuer URI to register with supported services. It never changes.
   */
  issuer?: string | undefined;
  /**
   * Public verification keys.
   */
  jwksUri?: string | undefined;
  /**
   * Published keys.
   */
  keys: Array<InternalSessionIssuerKey>;
  /**
   * Revoked keys whose signing material is not yet disabled. Speakeasy retries until it is.
   */
  kmsCleanupPending: number;
  /**
   * Set only by a compromised rotation whose replacement key could not be activated: the keys are contained, the issuer stays keyless, and repeating the call retries the replacement.
   */
  replacementError?: string | undefined;
  /**
   * When a pending rotation can be activated.
   */
  rotationReadyAt?: Date | undefined;
  /**
   * unavailable: not offered to this organization yet. verified_domain_required: needs at least one verified email domain. setup_required: not enabled yet. enabled: Speakeasy signs with it. disabled: an admin turned signing off. signing_paused: enabled, but Speakeasy has paused all signing. keyless: every key was revoked after a compromise and no replacement is active yet.
   */
  state: InternalSessionIssuerState;
};

/** @internal */
export const InternalSessionIssuerState$inboundSchema: z.ZodMiniType<
  InternalSessionIssuerState,
  unknown
> = openEnums.inboundSchema(InternalSessionIssuerState);

/** @internal */
export const InternalSessionIssuer$inboundSchema: z.ZodMiniType<
  InternalSessionIssuer,
  unknown
> = z.pipe(
  z.object({
    can_enable: z.boolean(),
    issuer: z.optional(z.string()),
    jwks_uri: z.optional(z.string()),
    keys: z.array(InternalSessionIssuerKey$inboundSchema),
    kms_cleanup_pending: z.int(),
    replacement_error: z.optional(z.string()),
    rotation_ready_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    state: InternalSessionIssuerState$inboundSchema,
  }),
  z.transform((v) => {
    return remap$(v, {
      "can_enable": "canEnable",
      "jwks_uri": "jwksUri",
      "kms_cleanup_pending": "kmsCleanupPending",
      "replacement_error": "replacementError",
      "rotation_ready_at": "rotationReadyAt",
    });
  }),
);

export function internalSessionIssuerFromJSON(
  jsonString: string,
): SafeParseResult<InternalSessionIssuer, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => InternalSessionIssuer$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'InternalSessionIssuer' from JSON`,
  );
}
